"use strict";

var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ToolbarBase = exports.OMIT_BACKGROUND_CLASS_NAME = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _core = require("@emotion/core");
var _styled = _interopRequireDefault(require("@emotion/styled"));
var _designTokens = require("@smartsheet/design-tokens");
var _lodestarUtils = require("@smartsheet/lodestar-utils");
var _react = _interopRequireWildcard(require("react"));
var _FocusContext = require("../context/FocusContext");
var _ToolbarSizeContext = require("../context/ToolbarSizeContext");
var _utils = require("../utils");
var _ToolbarCollapsibleButtonWrapper = require("./ToolbarCollapsibleButtonWrapper/ToolbarCollapsibleButtonWrapper");
const TOOLBAR_X_PADDING = 8;
const PERSISTENT_WRAPPER_MARGIN = 10;
const OMIT_BACKGROUND_CLASS_NAME = "omit-background";
exports.OMIT_BACKGROUND_CLASS_NAME = OMIT_BACKGROUND_CLASS_NAME;
const ToolbarContainmentPadding = "6px";
const ToolbarContainmentHeight = "28px";
const containmentVariantMap = (height, toolbarSize) => ({
  default: {
    backgroundColor: "transparent",
    borderRadius: "0",
    height: toolbarSize === "xxSmall" ? `${height}px` : `${height - _utils.TOOLBAR_VERT_PADDING * 2}px`,
    padding: toolbarSize === "xxSmall" ? "0" : `${_utils.TOOLBAR_VERT_PADDING}px 8px`
  },
  full: {
    backgroundColor: _designTokens.ursaColors.neutral00,
    borderRadius: "8px",
    height: ToolbarContainmentHeight,
    padding: ToolbarContainmentPadding
  }
});
const StyledToolbar = (0, _styled.default)("div")`
    box-sizing: content-box;

    // subtract vertical padding*2 to get correct height
    height: ${_ref => {
  let {
    containmentVariant,
    height,
    toolbarSize
  } = _ref;
  return containmentVariantMap(height, toolbarSize)[containmentVariant].height;
}};
    padding: ${_ref2 => {
  let {
    containmentVariant,
    height,
    toolbarSize
  } = _ref2;
  return containmentVariantMap(height, toolbarSize)[containmentVariant].padding;
}};
    background-color: ${_ref3 => {
  let {
    containmentVariant,
    height,
    toolbarSize
  } = _ref3;
  return containmentVariantMap(height, toolbarSize)[containmentVariant].backgroundColor;
}};
    border-radius: ${_ref4 => {
  let {
    containmentVariant,
    height,
    toolbarSize
  } = _ref4;
  return containmentVariantMap(height, toolbarSize)[containmentVariant].borderRadius;
}};

    flex-direction: row;
    display: flex;
    align-items: center;
    overflow: hidden;

    position: relative;
    display: flex;
    align-items: center;
    opacity: ${_ref5 => {
  let {
    shouldRender
  } = _ref5;
  return shouldRender ? 1 : 0;
}};
`;
const PersistentButtonsWrapper = (0, _styled.default)("div")`
    position: absolute;
    right: 0;
`;
const ToolbarBase = /*#__PURE__*/(0, _react.forwardRef)((_ref6, parentContainerRef) => {
  let {
    "aria-controls": ariaControls,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabeledBy,
    children,
    className,
    includeOverflowMenuSeparator,
    omitButtonHoverActiveStyles = false,
    overflowMenuClassName,
    overflowMenuFlyoutProps,
    overflowMenuIcon,
    overflowMenuText,
    overflowToolbarMaxWidth,
    overflowToolbarZIndex,
    clientId,
    minWidth = 0,
    contentAfter,
    size = "large",
    useWindowForOverflowMenuContainer,
    containmentVariant = "default",
    ...props
  } = _ref6;
  const {
    parentRef: focusRef,
    ensureFocusableElement
  } = (0, _FocusContext.useFocusContext)();
  const {
    isCollapsible
  } = (0, _react.useContext)(_ToolbarSizeContext.ToolbarSizeContext);
  // When a Toolbar is not collapsible, we'll want to immediately show it at 100% opacity.
  // When a Toolbar is collapsible, we need to wait until it has fully measured its
  // available space to figure out which buttons to render in the main Toolbar, and which
  // to render in the overflow Toolbar.
  const [shouldRender, setShouldRender] = (0, _react.useState)(!isCollapsible);
  const containerRef = (0, _react.useRef)(null);
  const persistentWrapperRef = (0, _react.useRef)(null);

  // Toolbar container width - persistent wrapper width - PERSISTENT_WRAPPER_MARGIN
  // Space available for toolbar buttons
  const [availableSpace, setAvailableSpace] = (0, _react.useState)(0);
  const calculateAvailableSpace = (0, _react.useCallback)(() => {
    let toolbarWidth = 0;
    let persistentWrapperWidth = 0;
    if (containerRef.current) {
      toolbarWidth = containerRef.current.getBoundingClientRect().width - TOOLBAR_X_PADDING * 2;
    }
    if (persistentWrapperRef.current) {
      persistentWrapperWidth = persistentWrapperRef.current.getBoundingClientRect().width + PERSISTENT_WRAPPER_MARGIN;
    }
    setAvailableSpace(toolbarWidth - persistentWrapperWidth);
  }, []);
  // watch resize on toolbar container
  const {
    ref: resizeObserverRef
  } = (0, _lodestarUtils.useResizeObserver)(calculateAvailableSpace, {
    debounceMs: 20,
    debounceMaxMs: 50
  });

  // watch resize on toolbar container
  const {
    ref: ensureFocusResizeObserverRef
  } = (0, _lodestarUtils.useResizeObserver)(ensureFocusableElement, {
    debounceMs: 50,
    debounceMaxMs: 100
  });
  const combinedRef = (0, _lodestarUtils.useForkRef)(parentContainerRef, containerRef, focusRef, resizeObserverRef, ensureFocusResizeObserverRef);
  return /*#__PURE__*/_react.default.createElement(_core.ClassNames, null, _ref7 => {
    let {
      cx
    } = _ref7;
    return /*#__PURE__*/_react.default.createElement(StyledToolbar, (0, _extends2.default)({
      "aria-controls": ariaControls,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabeledBy,
      className: cx(className, {
        [OMIT_BACKGROUND_CLASS_NAME]: omitButtonHoverActiveStyles
      }),
      minWidth: minWidth,
      role: "toolbar",
      ref: combinedRef,
      height: (0, _utils.getToolbarHeight)(size),
      toolbarSize: size,
      "data-client-id": clientId,
      shouldRender: shouldRender,
      containmentVariant: containmentVariant
    }, props), isCollapsible ? /*#__PURE__*/_react.default.createElement(_ToolbarCollapsibleButtonWrapper.ToolbarCollapsibleButtonWrapper, {
      availableSpace: availableSpace,
      zIndex: overflowToolbarZIndex,
      overflowMenuMaxWidth: overflowToolbarMaxWidth ??
      // if this is set, use it
      containerRef.current?.getBoundingClientRect().width ??
      // otherwise, use the container width
      window.innerWidth - 20 // will only be used if we don't have a current ref
      ,

      includeOverflowMenuSeparator: includeOverflowMenuSeparator,
      overflowMenuClassName: overflowMenuClassName,
      overflowMenuFlyoutProps: overflowMenuFlyoutProps,
      overflowMenuIcon: overflowMenuIcon,
      overflowMenuText: overflowMenuText,
      toolbarSize: size,
      setShouldRender: setShouldRender,
      useWindowForOverflowMenuContainer: useWindowForOverflowMenuContainer
    }, children) : children, contentAfter && /*#__PURE__*/_react.default.createElement(PersistentButtonsWrapper, {
      ref: persistentWrapperRef,
      "data-client-id": "toolbar-persistent"
    }, contentAfter));
  });
});
exports.ToolbarBase = ToolbarBase;
ToolbarBase.displayName = "Toolbar";
//# sourceMappingURL=ToolbarBase.js.map